import { Currency } from 'module/api/account/types';
import {
  SalesforceActions,
  SalesforceIntegrationId,
  RLProjectFieldMapping,
  ProjectFinancialsContractType,
} from 'module/api/integrations/salesforce/types';
import { Loading } from 'module/ui/components/atoms/loading';
import {
  useSalesForceActionFieldsQuery,
  useSalesForceTriggerObjectsQuery,
  useTemplatePlaceholders,
  useSalesForceFinancialsFieldsQuery,
} from 'queries/salesforce';
import { FC, useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { BlockList } from 'components/organisms/signal/detail/block-list/list';
import { ActionDrawer } from '../../create/action-drawer';
import { useAutomationContext } from '../../create/automation-context';
import { getFields } from '../../create/blocks/shared/block-utils';
import { RenderBlockDetails } from '../../create/blocks/shared/render-block-details';

const salesForceType = SalesforceActions.Project;

export const AutomationEditProjectBlock: FC = () => {
  const { integrationId } = useParams<SalesforceIntegrationId>();

  const { automationDetails, setAutomationDetails } = useAutomationContext();

  const [actionDrawerVisible, setActionDrawerVisible] = useState(false);
  const hasSyncedInitialDriversRef = useRef(false);
  const hasHydratedInitialFieldsRef = useRef(false);

  const { inputs } = automationDetails?.actions?.[0] || {};
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(
    typeof inputs?.templateId === 'string' ? inputs.templateId : null,
  );

  const { financialContractType, currency: currencyCode } = inputs || {};

  const [billingMethod, setBillingMethod] =
    useState<ProjectFinancialsContractType | null>(
      financialContractType as ProjectFinancialsContractType,
    );
  const [currency, setCurrency] = useState<
    Currency['currencyCode'] | undefined
  >(currencyCode as Currency['currencyCode']);

  const fieldsKey = getFields(salesForceType);

  const {
    salesforceObject: selectedSalesforceObject,
    salesForceType: selectedSalesForceType,
  } = automationDetails || {};

  const { value } = selectedSalesforceObject || {};

  const { data: salesforceObject } = useSalesForceTriggerObjectsQuery(
    parseInt(integrationId || '0'),
    {},
  );

  const { data: projectFields = [], isLoading, isFetching } =
    useSalesForceActionFieldsQuery(
      parseInt(integrationId || '0'),
      String(value) || salesforceObject?.[0]?.id || '',
      selectedSalesForceType ?? SalesforceActions.Project,
      undefined,
      {
        enabled: !!value || !!salesforceObject?.[0]?.id,
      },
    );

  const { data: placeholderFields, status: placeholderStatus } =
    useTemplatePlaceholders(
      Number(integrationId),
      selectedSalesforceObject?.value.toString() || '',
      currentTemplateId,
      {
        enabled: !!currentTemplateId,
      },
    );

  const { data: financialFields, isFetching: isFinancialsFieldsFetching } =
    useSalesForceFinancialsFieldsQuery(
      Number(integrationId),
      selectedSalesforceObject?.value.toString() || '',
      billingMethod!,
      currency!,
      {
        enabled: billingMethod != null && currency != null,
      },
    );

  const onSave = (updatedFields: RLProjectFieldMapping) => {
    const fields = Object.keys(updatedFields).reduce((acc, key) => {
      acc[key] =
        key !== 'teamMembers'
          ? updatedFields[key] != null
            ? {
              ...updatedFields[key],
              label:
                updatedFields[key]?.valueMeta?.fieldLabel ??
                updatedFields[key]?.label,
            }
            : undefined
          : updatedFields[key];

      return acc;
    }, {});

    setAutomationDetails({
      [fieldsKey]: fields,
    });
  };

  const handleTemplateChange = (templateId: string | null) => {
    setCurrentTemplateId(templateId);
  };

  const handleBillingMethodChange = (
    billingMethodValue: ProjectFinancialsContractType | null,
  ) => {
    setBillingMethod(billingMethodValue);
  };

  const handleCurrencyChange = (currencyValue: Currency['currencyCode']) => {
    setCurrency(currencyValue);
  };

  useEffect(() => {
    if (hasSyncedInitialDriversRef.current) {
      return;
    }
    if (inputs == null) {
      return;
    }
    if (typeof inputs.templateId === 'string') {
      setCurrentTemplateId(inputs.templateId);
    }
    if (typeof inputs.financialContractType === 'string') {
      setBillingMethod(
        inputs.financialContractType as ProjectFinancialsContractType,
      );
    }
    if (typeof inputs.currency === 'string') {
      setCurrency(inputs.currency as Currency['currencyCode']);
    }
    hasSyncedInitialDriversRef.current = true;
  }, [inputs]);

  // Merge all fields into rocketlaneFields on page load in edit mode
  useEffect(() => {
    const actionInputs = automationDetails?.actions?.[0]?.inputs;
    const requiresTemplateFields = Boolean(actionInputs?.templateId);
    const requiresFinancialFields = Boolean(
      actionInputs?.financialContractType && actionInputs?.currency,
    );
    const templateReady =
      !requiresTemplateFields || placeholderStatus === 'success';
    const financialReady =
      !requiresFinancialFields || !isFinancialsFieldsFetching;

    if (
      hasHydratedInitialFieldsRef.current ||
      projectFields.length === 0 ||
      !actionInputs ||
      !templateReady ||
      !financialReady
    ) {
      return;
    }

    {
      const allFields = [
        ...projectFields,
        ...(placeholderFields ?? []),
        ...(financialFields ?? []),
      ];

      const inputs = actionInputs;

      const selectedData = Object.keys(inputs).reduce((acc, each) => {
        const currentField = allFields.find((r) => r.fieldName === each);
        const existingValue = automationDetails?.[fieldsKey]?.[each];

        if (currentField) {
          const val = inputs[each];

          if (currentField.fieldName === 'teamMembers') {
            const teamMemberData = inputs[each] as string;
            const template: any = [];

            teamMemberData
              ?.toString()
              ?.split(',')
              .forEach((item) => {
                const member = currentField?.metaData?.options?.find(
                  (o) =>
                    String(o.valueStr ?? (o as any).value ?? '') ===
                    String(item ?? ''),
                );

                if (member) {
                  template.push({
                    ...member,
                    value: member.valueStr ?? (member as any).value,
                  });
                }
              });

            acc[each] = template;

            return acc;
          }

          const rlFieldValue = currentField?.metaData?.options?.find(
            (o) =>
              String(o.valueStr ?? (o as any).value ?? '') === String(val ?? ''),
          );

          acc[each] = {
            label: rlFieldValue?.label ?? (val as string),
            valueStr:
              String(rlFieldValue?.valueStr ?? (rlFieldValue as any)?.value ?? val),
            valueMeta: rlFieldValue?.valueMeta,
          };

          return acc;
        }

        if (existingValue != null) {
          if (each === 'teamMembers') {
            acc[each] = existingValue;

            return acc;
          }
          const existingValueStr =
            typeof existingValue === 'object' &&
              existingValue != null &&
              'valueStr' in (existingValue as any)
              ? String((existingValue as any).valueStr ?? '')
              : String(inputs[each] ?? '');
          acc[each] =
            typeof existingValue === 'object' &&
              existingValue != null &&
              'label' in (existingValue as any) &&
              Boolean((existingValue as any).label)
              ? existingValue
              : {
                ...(typeof existingValue === 'object' && existingValue != null
                  ? (existingValue as any)
                  : {}),
                label: existingValueStr,
                valueStr: existingValueStr,
              };
          return acc;
        }
        const val = inputs[each];
        if (val != null) {
          acc[each] = {
            label: String(val),
            valueStr: String(val),
          };
        }

        return acc;
      }, {});

      // Update if there are fields to merge
      if (Object.keys(selectedData).length > 0) {
        setAutomationDetails({
          [fieldsKey]: selectedData,
        });
      }
      hasHydratedInitialFieldsRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectFields,
    placeholderFields,
    financialFields,
    placeholderStatus,
    isFinancialsFieldsFetching,
  ]);

  return (
    <>
      <BlockList.Item
        disabled={!value || isLoading || isFetching}
        onClick={() => setActionDrawerVisible(true)}
      >
        <BlockList.Item.Label data-required="true">
          <BlockList.Item.Label.Text>
            <FormattedMessage
              id={'settings.integrations.create.automation.project.label'}
            />
          </BlockList.Item.Label.Text>
        </BlockList.Item.Label>
        <BlockList.Item.Content>
          {isLoading || isFetching ? (
            <Loading />
          ) : (
            <RenderBlockDetails
              actions={automationDetails?.[fieldsKey] ?? {}}
              fields={[
                ...projectFields,
                ...(placeholderFields ?? []),
                ...(financialFields ?? []),
              ]}
            />
          )}
        </BlockList.Item.Content>
      </BlockList.Item>
      <ActionDrawer
        label="project"
        fields={[
          ...projectFields,
          ...(placeholderFields ?? []),
          ...(financialFields ?? []),
        ]}
        fieldsStatus={isLoading || isFetching ? 'pending' : 'success'}
        visible={actionDrawerVisible}
        value={automationDetails?.[fieldsKey] ?? undefined}
        handleModalVisibility={setActionDrawerVisible}
        onSave={onSave}
        onClose={() => setActionDrawerVisible(false)}
        placeholderFields={placeholderFields}
        placeholderStatus={placeholderStatus}
        financialFields={financialFields}
        isFinancialsFieldsFetching={isFinancialsFieldsFetching}
        onTemplateChange={handleTemplateChange}
        onBillingMethodChange={handleBillingMethodChange}
        onCurrencyChange={handleCurrencyChange}
      />
    </>
  );
};
